import { useEffect } from "react";
import { useLocation } from "wouter";
import { ShieldAlert } from "lucide-react";
import { isUnauthorizedError, isForbiddenError } from "@/lib/queryClient";

/**
 * Wraps admin-only page content and reacts to the access errors thrown by the
 * admin endpoints (`requireAdmin` / `requireAppAdmin`), which now distinguish
 * 401 from 403:
 *
 * - 401 (Unauthorized): the session is missing or expired, so the user is sent
 *   to the login screen rather than shown a permission message.
 * - 403 (Forbidden): the user is logged in but lacks admin permission, so a
 *   clear "you don't have permission" message is rendered and the user stays
 *   logged in.
 *
 * Any other state (no error, or an unrelated error) renders `children` so the
 * page's own loading/error handling continues to apply.
 */
export function AdminAccessGuard({
  error,
  children,
}: {
  error: unknown;
  children: React.ReactNode;
}) {
  const [, navigate] = useLocation();
  const unauthorized = isUnauthorizedError(error);
  const forbidden = isForbiddenError(error);

  useEffect(() => {
    if (unauthorized) {
      navigate("/app/login");
    }
  }, [unauthorized, navigate]);

  if (unauthorized) {
    return null;
  }

  if (forbidden) {
    return (
      <div
        className="flex flex-col items-center justify-center py-20 px-6 text-center"
        data-testid="admin-access-denied"
      >
        <ShieldAlert className="h-10 w-10 text-brand-amber mb-3" />
        <h2 className="text-lg font-semibold" data-testid="text-admin-access-denied-title">
          You don't have permission to view this page
        </h2>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          This area is restricted to administrators. If you believe you should have
          access, contact your account owner or support@padpulse.io.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
